/**
 * Gallery Creature Card Component
 *
 * TASK-GBCS-2: Individual draggable creature card for gallery
 * TASK-TDI-1: Added tap-to-select support for mobile
 *
 * Features:
 * - Displays creature sprite thumbnail (64x64)
 * - Shows creature name
 * - Draggable via HTML5 Drag & Drop API (desktop)
 * - Tap-to-select support (mobile)
 * - Visual feedback during drag and selection
 */

import React, { useRef, useCallback } from 'react';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import './GalleryCreatureCard.css';

interface GalleryCreatureCardProps {
  creature: SerializedCreatureDocument;
  disabled?: boolean;
  deployed?: boolean;
  isSelected?: boolean;
  onDragStart: (creature: SerializedCreatureDocument) => void;
  onDragEnd: () => void;
  onSelect?: (creature: SerializedCreatureDocument) => void;
  onViewDetails?: () => void;
}

export const GalleryCreatureCard: React.FC<GalleryCreatureCardProps> = ({
  creature,
  disabled = false,
  deployed = false,
  isSelected = false,
  onDragStart,
  onDragEnd,
  onSelect,
  onViewDetails
}) => {
  // Track if we're dragging to prevent click from firing after drag
  const isDraggingRef = useRef(false);
  // Track touch start position for iOS tap detection
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  // Handle selection (shared by click and touch)
  const handleSelect = useCallback(() => {
    console.log('[GalleryCreatureCard] handleSelect called for:', creature.name, 'disabled:', disabled);

    // Don't fire if disabled
    if (disabled) {
      console.log('[GalleryCreatureCard] Blocked - card is disabled');
      return;
    }

    // Call onSelect if provided (for mobile tap-to-select)
    if (onSelect) {
      console.log('[GalleryCreatureCard] Calling onSelect for:', creature.name);
      onSelect(creature);
    }
  }, [creature, disabled, onSelect]);

  const handleClick = () => {
    // Don't fire click if we just finished dragging
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      return;
    }

    handleSelect();
  };

  // iOS Safari touch handling - onClick doesn't work reliably with draggable elements
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    console.log('[GalleryCreatureCard] Touch start:', creature.name);
  }, [creature.name]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.time;

    console.log('[GalleryCreatureCard] Touch end:', creature.name,
      'deltaX:', deltaX, 'deltaY:', deltaY, 'deltaTime:', deltaTime);

    // If touch was quick (<300ms) and didn't move much (<10px), treat as tap
    if (deltaTime < 300 && deltaX < 10 && deltaY < 10) {
      console.log('[GalleryCreatureCard] Detected tap on:', creature.name);
      // Prevent the click event from also firing
      e.preventDefault();
      handleSelect();
    }

    touchStartRef.current = null;
  }, [creature.name, handleSelect]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    if (disabled) {
      e.preventDefault();
      return;
    }

    // Set drag data
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'gallery-creature',
      creatureId: creature.id
    }));

    // Create drag image from the sprite
    const dragImage = document.createElement('img');
    dragImage.src = creature.sprites.menuSprite;
    dragImage.style.width = '64px';
    dragImage.style.height = '64px';
    e.dataTransfer.setDragImage(dragImage, 32, 32);

    onDragStart(creature);
  };

  const handleDragEnd = () => {
    // Keep isDraggingRef true briefly so click handler can check it
    // The click handler will reset it
    onDragEnd();
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click from firing
    if (onViewDetails) {
      onViewDetails();
    }
  };

  const classNames = [
    'gallery-creature-card',
    disabled ? 'disabled' : '',
    deployed ? 'deployed' : '',
    isSelected ? 'selected' : ''
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="creature-sprite">
        <img
          src={creature.sprites.menuSprite}
          alt={creature.name}
          draggable={false}
        />
      </div>
      <div className="creature-name">{creature.name}</div>

      {/* Info Button */}
      {onViewDetails && (
        <button
          className="creature-info-button"
          onClick={handleInfoClick}
          aria-label="View creature details"
          title="View details"
        >
          ℹ️
        </button>
      )}
    </div>
  );
};
